import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import useArticleSection from "@/components/business/FillInTableContainer/hooks/useArticleSection";
import { useDesDealTypeDropdownDisplayNameValue } from "@/contexts/desDealTypeDropdownDisplayNameContext";
import {
  ArticleEntryData,
  ArticleSection,
} from "@/types/articleEntryData.types";
import {
  DealLaScDealTermsRightsFlowShellKeys,
  RightsScopeTableFlowValues,
} from "@/types/dealLaSc.types";
import { clinicalTrialsIdentifierApis } from "@api/clinicalTrialsIdentifier/clinicalTrialsIdentifier.service";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  PositionInfo,
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import {
  getMaxShellValueLength,
  isEnterTableNotes,
  isDisabledInsertText,
} from "@FillInTableContainer/shared/utils/getOptionsFromData";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";
import { normalizeWhitespaceAndTrim } from "@utils/normalizeWhitespaceAndTrim";

import useCheckIsActiveDropdownKeyboardNavigation from "./useCheckIsActiveDropdownKeyboardNavigation";
import { useGetInfoOfShell } from "./useGetInfoOfShell";
import { useHandleInfoOfDropdownLists } from "./useHandleInfoOfDropdownLists";
import {
  DEFAULT_TARGET_INDEX,
  TargetListType,
  useHandleTargetListIndex,
} from "./useHandleTargetListIndex";
import {
  AIDropdownListFormat,
  DataSetDropdownList,
  DefaultDataSetType,
  DictionaryCategoryDropdownList,
  DictionaryDefaultDropdownList,
  DictionaryList,
} from "../components/TableCreatorListBox/types/tableCreatorDropdown.types";
import { CLINICAL_TRIALS_IDENTIFIER_VALIDATION_INFOS } from "../components/TableCreatorTextFieldContainer/components/ClinicalTrialsIdentifierValidationInfo/constants/clinicalTrialsIdentifiers.constants";
import {
  DATA_SET_DROPDOWN_KEYS,
  UPDATE_DATA_SET,
} from "../constants/dataSetDropdown.constants";
import { MULTI_SELECT_RIGHTS_SCOPE_SHELL_KEYS } from "../constants/multiSelectShell.constants";
import { MULTI_SELECT_PATH_TO_SET_VALUES } from "../constants/tableCreatorMultiSelect.constants";
import { SHELL_PATH_TO_READ_SHELL_INFO } from "../constants/tableCreatorShell.constants";
import { TEXTFIELD_PATH_TO_ENTER_ENTRY_DATA } from "../constants/tableCreatorTextField.constants";
import { useDatasetDropdownState } from "../stores/datasetDropdown";
import { changeFormatAiDropdownList } from "../utils/changeFormatAiDropdownList";

import type {
  EntryDataValueAndOptions,
  EntryDataValueInfo,
} from "@/types/clinicalTrials.types";

interface UseHandleTextFieldInputEventReturn {
  selectedValue: string;
  validationMessage: [boolean, string] | null;
  textFieldTextareaRef: React.RefObject<HTMLDivElement>;
  targetListIndex: TargetListType;
  valueInfo: EntryDataValueInfo;
  aiList: AIDropdownListFormat[];
  dictionaryList: DictionaryList;
  dataSetList: DataSetDropdownList[];
  isFocusInputField: boolean;
  isDictionaryList: boolean;
  isOriginalAiList: boolean;
  isOriginalDictionaryList: boolean;
  isDataSetList: boolean;
  copyStatus: "default" | "success" | "error";
  handleChangedEnterValue: (event: React.FormEvent<HTMLDivElement>) => void;
  handleEnterValueKeyboard: (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => void;
  changeSelectedValue: (selectedItem: string) => void;
  handleFocusTextField: (event: React.FocusEvent<HTMLDivElement>) => void;
  handleFocusTextFieldAndOpenListBox: () => void;
  handleBlurTextField: () => void;
  checkIndexOfValueInDefaultAiList: () => number;
  handleFocusOutTextField: () => void;
  handleChangeDataSetValue: (selectItem: DefaultDataSetType) => void;
  deleteCurrentTextField: (event: React.MouseEvent<HTMLButtonElement>) => void;
  copyCurrentTextValue: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => Promise<void>;
  changeMultiSelectValues: (items: string[]) => void;
  getRawMultiSelectValues: () => string[];
}

export const useHandleTextFieldInputEvent = (
  positionInfo: PositionInfo,
  shellValue: EntryDataValueAndOptions,
  articleSectionKey?: ArticleSection,
): UseHandleTextFieldInputEventReturn => {
  const textFieldTextareaRef = useRef<HTMLDivElement>(null);

  const draftArticleId = getPressIdFromURL();
  const articleSectionByClassification = useArticleSection();
  const articleSection = articleSectionKey || articleSectionByClassification;
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { checkExistNextLine } = useCheckIsActiveDropdownKeyboardNavigation();

  const {
    targetListIndex,
    changeTargetListIndex,
    arrowDownChangeIndex,
    arrowUpChangeIndex,
  } = useHandleTargetListIndex();

  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { changeValidatedInfo } = useValidatedInTableCreator();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { shellValue: getShellValue } = useGetInfoOfShell(
    positionInfo.groupTableKey,
    articleSection,
  );
  const { changeDealTypeTextToDisplayName } =
    useDesDealTypeDropdownDisplayNameValue();

  const currentTextareaInput = textFieldTextareaRef?.current?.innerText || "";

  const {
    groupTableKey,
    groupTableIndex,
    tableKey,
    tableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellKey,
    subGroupShellIndex,
    shellKey,
    shellIndex,
    valueIndex,
  } = positionInfo;

  const targetShellKey =
    groupTableKey === "Licensee Table" && shellKey === "Party Axis"
      ? "Party Axis MA"
      : shellKey;

  const {
    initialDropdownList,
    initialDataSetList,
    dictionaryList,
    aiList,
    dataSetList,
    isDictionaryList,
    isCategoryList,
    isDataSetList,
    isOriginalAiList,
    isOriginalDictionaryList,
    changeDictionaryList,
    changeAiList,
    changeDatasetDropdownList,
    currentDictionaryStringList,
    currentAiStringList,
    currentShellKeyDatasetList,
  } = useHandleInfoOfDropdownLists({
    shellValue,
    shellKey: targetShellKey,
    groupTableKey,
  });

  const valueInfo = shellValue.value[valueIndex || 0] || [
    { text: "", is_modified: true },
  ];

  const currentDictionaryList = currentDictionaryStringList;
  const currentAiList = currentAiStringList;
  const entireDropdownList = useMemo(
    () => [...dataSetList, ...aiList, ...dictionaryList],
    [dataSetList, aiList, dictionaryList],
  );

  const [entryData, setEntryData] = useEntryDataState(draftArticleId);
  const [selectedDataset, setSelectedDataset] =
    useDatasetDropdownState(groupTableKey);
  const [enterValue, setEnterValue] = useState<string>(valueInfo.text);
  const [validationMessage, setValidationMessage] = useState<
    [boolean, string] | null
  >(null);
  const [isFocusInputField, setIsFocusInputField] = useState<boolean>(false);
  const [copyStatus, setCopyStatus] = useState<"default" | "success" | "error">(
    "default",
  );

  const textValueRef = useRef<string>("");

  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const checkedNCTNameValidation = async (currentText?: string) => {
    if (!enterValue) return;
    if (valueInfo.text === "") return;

    try {
      const response = await clinicalTrialsIdentifierApis.get(
        currentText || enterValue,
      );

      if (response.data.passed) {
        setValidationMessage([true, response.data.dtlCdNm]);
      } else {
        const formattedValue = CLINICAL_TRIALS_IDENTIFIER_VALIDATION_INFOS.find(
          (item) => item.id === response.data.dtlCdId,
        )?.format;
        setValidationMessage([false, `Enter in [ ${formattedValue} ] format.`]);
      }
    } catch (error) {
      setValidationMessage([
        false,
        "The format entered is incorrect. Please check.",
      ]);
    }
  };

  const copyCurrentTextValue = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    try {
      await navigator.clipboard.writeText(enterValue);
      setCopyStatus("success");

      copyTimeoutRef.current = setTimeout(() => {
        setCopyStatus("default");
      }, 1000);
    } catch (error) {
      setCopyStatus("error");

      copyTimeoutRef.current = setTimeout(() => {
        setCopyStatus("default");
      }, 1000);
    }
  };

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleFocusOutTextField = () => {
    setIsFocusInputField(false);
    setSelectedDataset({});
    changeTargetListIndex(DEFAULT_TARGET_INDEX);
    changeInfo(DEFAULT_POSITION_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);

    if (
      getMaxShellValueLength(articleSection, shellKey || "") > 1 &&
      valueIndex &&
      valueIndex > shellValue.value.length - 1
    ) {
      if (textFieldTextareaRef.current)
        textFieldTextareaRef.current.innerText = "";
    }

    if (shellKey === "NCT Name") {
      const isSameValue = shellValue.value.some(
        (item) => item.text === enterValue,
      );
      if (isSameValue) return;

      checkedNCTNameValidation();
    }

    changeAiList(changeFormatAiDropdownList(currentAiStringList, shellKey));
    changeDictionaryList(initialDropdownList);
    changeDatasetDropdownList(initialDataSetList);
  };

  const focusEventToChangePositionInfo = () => {
    changeInfo(positionInfo);
    changeFocusStatus({
      type: "focusInput",
      container: "textField",
    });
    setIsFocusInputField(true);
    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
  };

  const changedEnterOrSelectedValue = (
    text: string,
    selectShellKey?: string,
  ) => {
    const valueText = normalizeWhitespaceAndTrim(text);

    if (groupTableKey === "") return;
    const isAbleCheckValue =
      shellKey !== null && shellIndex !== null && valueIndex !== null;

    const currentShellKey = selectShellKey || shellKey;

    const changedEntryData =
      TEXTFIELD_PATH_TO_ENTER_ENTRY_DATA?.[articleSection]?.[groupTableKey] &&
      isAbleCheckValue
        ? TEXTFIELD_PATH_TO_ENTER_ENTRY_DATA[articleSection][groupTableKey]?.(
            entryData,
            tableIndex,
            tableKey,
            groupShellKey,
            groupShellIndex,
            subGroupShellIndex,
            currentShellKey as string,
            shellIndex,
            valueText,
            valueIndex,
            [...currentDictionaryList, ...currentAiList],
          )
        : {};

    if (changedEntryData) {
      changeAiList(changeFormatAiDropdownList(currentAiStringList, shellKey));
      changeDatasetDropdownList(initialDataSetList);
      changeDictionaryList(initialDropdownList);
      changeTargetListIndex(DEFAULT_TARGET_INDEX);
      setEntryData(() => changedEntryData);
      changeCurrentHistory({
        entryData: changedEntryData,
        changedDataInfo: {
          target: "value",
          action: "update",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey,
            groupShellIndex,
            shellKey,
            shellIndex,
            value: valueText,
            valueIndex,
          },
        },
      });

      handleFocusOutTextField();
    }
  };

  const changeAiListBasedOnEnteredValue = (enteredValue: string) => {
    const currentOptions = shellValue.options;
    const lowerCaseValue = enteredValue.toLowerCase();
    const aiListIncludingValue =
      enteredValue === ""
        ? currentOptions
        : currentOptions.filter((option) =>
            option.toLowerCase().includes(lowerCaseValue),
          );
    changeAiList(changeFormatAiDropdownList(aiListIncludingValue, shellKey));
    changeTargetListIndex(DEFAULT_TARGET_INDEX);
  };

  const handleCompareCurrentInputDictionaryDropdown = (
    currentInput: string,
  ) => {
    if (isCategoryList) {
      const currentDropdownList =
        initialDropdownList as DictionaryCategoryDropdownList[];

      const updateIncludesCurrentInputList = currentDropdownList.map(
        (dropdown) => ({
          ...dropdown,
          list: dropdown.list
            .map((category) => ({
              ...category,
              list: category.list.filter((listItem) =>
                listItem.toLocaleLowerCase().includes(currentInput),
              ),
            }))
            .filter((listItem) => listItem.list.length > 0),
        }),
      );

      return updateIncludesCurrentInputList;
    }

    const currentDropdownList =
      initialDropdownList as DictionaryDefaultDropdownList[];

    const updateIncludesCurrentInputList = currentDropdownList.map(
      (dropdown) => ({
        ...dropdown,
        list: dropdown.list.filter((listItem) =>
          listItem.toLocaleLowerCase().includes(currentInput),
        ),
      }),
    );

    return updateIncludesCurrentInputList;
  };

  const handleCompareEnteredValueDatasetList = (lowerEnteredValue: string) => {
    const updateIncludesEnteredValue = initialDataSetList.map((dataset) => ({
      ...dataset,
      list: dataset.list.filter((datasetItem) => {
        return datasetItem[shellKey || ""]
          .toLocaleLowerCase()
          .includes(lowerEnteredValue);
      }),
    }));

    return updateIncludesEnteredValue;
  };

  const changeDatasetListBasedOnEnteredValue = (enteredValue: string) => {
    if (!currentShellKeyDatasetList.length) return;

    const replicateDatasetList = initialDataSetList;
    const lowerCaseEnteredValue = enteredValue.toLocaleLowerCase();

    const datasetIncludingValue =
      enteredValue === ""
        ? replicateDatasetList
        : handleCompareEnteredValueDatasetList(lowerCaseEnteredValue);

    changeDatasetDropdownList(datasetIncludingValue);
    changeTargetListIndex(DEFAULT_TARGET_INDEX);
  };

  const changeDictionaryListBasedOnEnteredValue = (enteredValue: string) => {
    const originalDictionaryList = initialDropdownList;
    const lowerCaseValue = enteredValue.toLowerCase();

    const dictionaryListIncludingValue =
      enteredValue === ""
        ? originalDictionaryList
        : handleCompareCurrentInputDictionaryDropdown(lowerCaseValue);

    changeDictionaryList(dictionaryListIncludingValue);
    changeTargetListIndex(DEFAULT_TARGET_INDEX);
  };

  const handleChangedEnterValue = (event: React.FormEvent<HTMLDivElement>) => {
    if (groupTableKey === "") return;

    const { innerText, textContent } = event.currentTarget;

    if (isEnterTableNotes(articleSection, groupTableKey)) {
      try {
        const selection = window.getSelection();
        const cursorPosition = selection?.focusOffset || 0;

        const isNeedToChangeSymbol = textContent?.match(/\+\+/g);
        const transformedValueToRemark = textContent
          ? textContent.replace(/\+\+/g, "†")
          : "";

        textValueRef.current = transformedValueToRemark;

        if (textFieldTextareaRef.current) {
          textFieldTextareaRef.current.textContent = textValueRef.current;

          if (selection) {
            try {
              const textNode =
                textFieldTextareaRef.current.firstChild ||
                textFieldTextareaRef.current;

              let newPosition = cursorPosition;
              if (isNeedToChangeSymbol && cursorPosition > 0) {
                newPosition = cursorPosition - 1;
              }

              const textLength = textNode.textContent?.length || 0;
              newPosition = Math.min(newPosition, textLength);

              const range = document.createRange();
              range.setStart(textNode, newPosition);
              range.collapse(true);
              selection.removeAllRanges();
              selection.addRange(range);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error("커서 위치 복원 중 오류 발생:", error);
            }
          }
        }

        setEnterValue(textValueRef.current);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("텍스트 변경 중 오류 발생:", error);
      }
    } else if (!isEnterTableNotes(articleSection, groupTableKey)) {
      textValueRef.current = innerText;

      setEnterValue(textValueRef.current);

      if (
        DATA_SET_DROPDOWN_KEYS.includes(groupTableKey) &&
        Object.values(selectedDataset).length > 1
      ) {
        setSelectedDataset({
          ...selectedDataset,
          [shellKey as string]: textValueRef.current,
        });
      }
    }

    focusEventToChangePositionInfo();
    changeAiListBasedOnEnteredValue(innerText);
    changeDictionaryListBasedOnEnteredValue(innerText);
    changeDatasetListBasedOnEnteredValue(innerText);
  };

  /**
   * Multi-select 대상 쉘의 현재 raw 값 목록을 반환한다.
   * SHELL_PATH_TO_READ_SHELL_INFO가 "____"로 join하기 때문에
   * entryData에서 직접 읽는다.
   */
  const getRawMultiSelectValues = (): string[] => {
    if (!MULTI_SELECT_RIGHTS_SCOPE_SHELL_KEYS.includes(shellKey as never)) {
      return [];
    }

    const rightsScopeTable =
      entryData?.[articleSection as keyof typeof entryData] &&
      "Rights Scope Table" in
        (entryData[articleSection as keyof typeof entryData] as object)
        ? (
            entryData[articleSection as keyof typeof entryData] as Record<
              string,
              unknown
            >
          )?.["Rights Scope Table"]
        : null;

    if (!Array.isArray(rightsScopeTable)) return [];

    const tableItem = rightsScopeTable[subGroupShellIndex || 0] as Record<
      string,
      { value: { text: string }[] }
    > | null;

    if (!tableItem) return [];

    if (shellKey === "Rights Scope") {
      return tableItem["Rights Scope"]?.value.map((v) => v.text) ?? [];
    }

    if (shellKey === "Benefiting Axis" || shellKey === "Contributing Axis") {
      const rightsFlow = tableItem[
        "Rights Flow"
      ] as unknown as RightsScopeTableFlowValues;
      return (
        rightsFlow?.[
          shellKey as DealLaScDealTermsRightsFlowShellKeys
        ]?.value.map((v) => v.text) ?? []
      );
    }

    return [];
  };

  /**
   * Multi-select 쉘의 선택 값 전체를 교체한다.
   * 드롭다운을 닫지 않으므로 연속 선택이 가능하다.
   */
  const changeMultiSelectValues = (items: string[]) => {
    if (groupTableKey === "") return;

    const changedEntryData = MULTI_SELECT_PATH_TO_SET_VALUES[articleSection]?.[
      groupTableKey
    ]?.(entryData, subGroupShellIndex || 0, shellKey || "", items);

    if (!changedEntryData) return;

    setEntryData(() => changedEntryData);
    changeCurrentHistory({
      entryData: changedEntryData,
      changedDataInfo: {
        target: "value",
        action: "update",
        position: {
          groupTableKey,
          groupTableIndex,
          tableKey,
          tableIndex,
          groupShellKey,
          groupShellIndex,
          shellKey,
          shellIndex,
          value: items.join(", "),
          valueIndex: 0,
        },
      },
    });
  };

  const handleChangeDataSet = (
    selectedValue: DefaultDataSetType,
    isFocusOut = true,
  ) => {
    const buildKeyCommentListEntryData = (): ArticleEntryData => {
      const idx = subGroupShellIndex ?? 0;
      const currentList = entryData.Deals?.["Key Comment List"] ?? [];
      return {
        ...entryData,
        Deals: {
          ...entryData.Deals,
          "Key Comment List": currentList.map((item, i) =>
            i === idx
              ? {
                  ...item,
                  "Comment Quote": {
                    ...item["Comment Quote"],
                    value: [
                      {
                        text: selectedValue["Comment Quote"],
                        is_modified: true,
                      },
                    ],
                  },
                  "Comment Spokesperson": {
                    ...item["Comment Spokesperson"],
                    value: [
                      {
                        text: selectedValue["Comment Spokesperson"],
                        is_modified: true,
                      },
                    ],
                  },
                }
              : item,
          ),
        },
      } as ArticleEntryData;
    };

    const baseEntryData =
      groupTableKey === "Key Comment List"
        ? buildKeyCommentListEntryData()
        : UPDATE_DATA_SET["Key Comment"](
            articleSection,
            entryData,
            selectedValue,
          );

    const changedEntryData =
      groupTableKey === "Key Comment" &&
      articleSection === "Deals" &&
      baseEntryData
        ? (() => {
            const currentList =
              (baseEntryData as ArticleEntryData).Deals?.["Key Comment List"] ??
              [];
            const firstItem = currentList[0] ?? {
              "Comment Quote": { value: [], options: [] },
              "Comment Spokesperson": { value: [], options: [] },
            };
            const syncedFirstItem = {
              ...firstItem,
              "Comment Quote": {
                ...firstItem["Comment Quote"],
                value: [{ text: selectedValue.Quote, is_modified: true }],
              },
              "Comment Spokesperson": {
                ...firstItem["Comment Spokesperson"],
                value: [
                  { text: selectedValue.Spokesperson, is_modified: true },
                ],
              },
            };
            const newList =
              currentList.length > 0
                ? currentList.map((item, idx) =>
                    idx === 0 ? syncedFirstItem : item,
                  )
                : [syncedFirstItem];
            return {
              ...(baseEntryData as ArticleEntryData),
              Deals: {
                ...(baseEntryData as ArticleEntryData).Deals,
                "Key Comment List": newList,
              },
            } as ArticleEntryData;
          })()
        : baseEntryData;

    if (changedEntryData) {
      changeTargetListIndex(DEFAULT_TARGET_INDEX);
      setEntryData(changedEntryData);
      changeCurrentHistory({
        entryData: changedEntryData,
        changedDataInfo: {
          target: "groupTable",
          action: "update",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey,
            groupShellIndex,
            shellKey,
            shellIndex,
            value: null,
            valueIndex,
          },
        },
      });

      if (isFocusOut) handleFocusOutTextField();
    }

    changeAiList(changeFormatAiDropdownList(currentAiStringList, shellKey));
    changeDictionaryList(initialDropdownList);
    changeDatasetDropdownList(initialDataSetList);
  };

  const handleChangeDataSetValue = (
    selectedValue: DefaultDataSetType,
    isFocusOut = true,
  ) => {
    handleChangeDataSet(selectedValue, isFocusOut);
    setSelectedDataset(selectedValue);
  };

  const handleChangeArrowEventDatasetValue = (
    selectedValue: DefaultDataSetType,
  ) => {
    setSelectedDataset(selectedValue);
    setEnterValue(selectedValue[shellKey as string]);
  };

  const handleChangeFocusDropdownValue = (
    currentValue: string | DefaultDataSetType,
  ) => {
    if (!textFieldTextareaRef.current) return;

    if (typeof currentValue === "object")
      return handleChangeArrowEventDatasetValue(currentValue);

    setSelectedDataset({});
    setEnterValue(currentValue);
  };

  const handleEnterValueKeyboard = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (
      isDisabledInsertText(articleSection, shellKey) &&
      event.key !== "Enter" &&
      event.key !== "ArrowDown" &&
      event.key !== "ArrowUp"
    ) {
      event.preventDefault();
      event.stopPropagation();

      return;
    }

    if (textFieldTextareaRef.current) {
      if (!event.shiftKey && event.key === "Enter") {
        event.preventDefault();
        if (
          Object.values(selectedDataset).length > 1 &&
          DATA_SET_DROPDOWN_KEYS.includes(groupTableKey)
        ) {
          handleChangeDataSet(selectedDataset, true);
          return textFieldTextareaRef.current.blur();
        }

        const isMultiSelect = MULTI_SELECT_RIGHTS_SCOPE_SHELL_KEYS.includes(
          shellKey as never,
        );
        if (isMultiSelect && enterValue) {
          const currentValues = getRawMultiSelectValues();
          const isAlreadySelected = currentValues.includes(enterValue);
          const newValues = isAlreadySelected
            ? currentValues.filter((v) => v !== enterValue)
            : [...currentValues, enterValue];
          changeMultiSelectValues(newValues);
          return;
        }

        changedEnterOrSelectedValue(enterValue || "");
        textFieldTextareaRef.current.innerText =
          changeDealTypeTextToDisplayName(shellKey, enterValue);
        textFieldTextareaRef.current.blur();
      }
    }

    if (!isDictionaryList && !isDataSetList && !aiList.length) return;

    if (event.key === "ArrowDown") {
      if (!textFieldTextareaRef.current) return;

      const currentCaretSelectionStatus = checkExistNextLine({
        textFieldRefCurrent: textFieldTextareaRef.current,
      });

      const { isHaveNextBottomCaretLine } = currentCaretSelectionStatus;

      if (!isHaveNextBottomCaretLine || !currentTextareaInput)
        event.preventDefault();

      if (!isHaveNextBottomCaretLine || !currentTextareaInput) {
        arrowDownChangeIndex({
          entireDropdownList,
          handleChangeFocusDropdownValue,
          event,
          currentCaretSelectionStatus,
          currentTextareaInput,
        });
      }
    }

    if (event.key === "ArrowUp") {
      if (!textFieldTextareaRef.current) return;

      const currentCaretSelectionStatus = checkExistNextLine({
        textFieldRefCurrent: textFieldTextareaRef.current,
      });

      const { isHaveNextTopCaretLine } = currentCaretSelectionStatus;

      if (!isHaveNextTopCaretLine || !currentTextareaInput)
        event.preventDefault();

      if (!isHaveNextTopCaretLine || !currentTextareaInput) {
        arrowUpChangeIndex({
          entireDropdownList,
          handleChangeFocusDropdownValue,
          event,
          currentCaretSelectionStatus,
          currentTextareaInput,
        });
      }
    }
  };

  const handleBlurTextField = () => {
    if (!textFieldTextareaRef.current) return;

    // Multi-select 쉘은 changeMultiSelectValues로 값을 직접 저장하므로
    // blur 시 enterValue 저장을 건너뜀 (드롭다운도 닫지 않음)
    if (MULTI_SELECT_RIGHTS_SCOPE_SHELL_KEYS.includes(shellKey as never)) {
      return;
    }

    const targetPathInEntryData = SHELL_PATH_TO_READ_SHELL_INFO[
      articleSection
    ]?.[shellKey || ""]?.(
      entryData,
      tableIndex,
      groupShellIndex || 0,
      shellIndex || 0,
      subGroupShellIndex || 0,
    )?.value;
    const targetValueInEntryData =
      targetPathInEntryData?.[valueIndex || 0]?.text || "";

    if (
      changeDealTypeTextToDisplayName(shellKey, targetValueInEntryData) ===
      textFieldTextareaRef.current.innerText
    )
      return;

    if (Object.values(selectedDataset).length > 1) {
      handleChangeDataSet(selectedDataset, true);
      return setIsFocusInputField(false);
    }

    changedEnterOrSelectedValue(enterValue || "");
    setIsFocusInputField(false);
  };

  const deleteCurrentTextField = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (textFieldTextareaRef.current) {
      textFieldTextareaRef.current.innerText = "";

      changedEnterOrSelectedValue("");
      textFieldTextareaRef.current.blur();
    }
  };

  const isFocused =
    info.tableKey === tableKey &&
    info.tableIndex === tableIndex &&
    info.groupShellKey === groupShellKey &&
    info.groupShellIndex === groupShellIndex &&
    info.subGroupShellIndex === subGroupShellIndex &&
    info.subGroupShellKey === subGroupShellKey &&
    info.shellKey === shellKey &&
    info.shellIndex === shellIndex &&
    info.valueIndex === valueIndex;

  const changeSelectedValue = (selectedItem: string) => {
    setEnterValue(changeDealTypeTextToDisplayName(shellKey, selectedItem));

    changedEnterOrSelectedValue(
      changeDealTypeTextToDisplayName(shellKey, selectedItem),
    );
  };

  const handleFocusTextField = (event: React.FocusEvent<HTMLDivElement>) => {
    event.stopPropagation();

    focusEventToChangePositionInfo();
  };

  const handleFocusTextFieldAndOpenListBox = () => {
    if (isFocusInputField) {
      handleFocusOutTextField();
      return;
    }
    focusEventToChangePositionInfo();
  };

  const checkIndexOfValueInDefaultAiList = () => {
    if (
      valueInfo.is_modified === false &&
      currentAiList.includes(valueInfo.text)
    ) {
      const targetIndexInAiList = currentAiList.indexOf(valueInfo.text);
      return targetIndexInAiList;
    }
    return -1;
  };

  const changeTargetDatasetDropdownList = useCallback(() => {
    if (!currentShellKeyDatasetList.length) return;

    const isSameDatasetListItem = initialDataSetList.some((datasetItem) =>
      datasetItem.list.some((setItem, setItemIndex) => {
        const compareShellValueList = Object.entries(setItem).map(
          ([dataShellKey, setValue]) => {
            const currentEntryDataValue = getShellValue(
              dataShellKey,
              tableIndex,
              groupShellIndex || 0,
              shellIndex || 0,
            )?.value[valueIndex || 0]?.text;

            if (setValue === currentEntryDataValue) return true;

            return false;
          },
        );

        const isSameDatasetAndShellValue = compareShellValueList.every(
          (isSame) => isSame === true,
        );

        if (isSameDatasetAndShellValue) {
          changeTargetListIndex({
            mainIndex: 0,
            listIndex: setItemIndex,
            categoryIndex: 0,
            key: "DATASET",
          });
        }

        return isSameDatasetAndShellValue;
      }),
    );

    return isSameDatasetListItem;

    // NOTE : getShellValue에 useCallback 을 사용하는 대신 의존성에서 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    changeTargetListIndex,
    currentShellKeyDatasetList.length,
    dataSetList,
    groupShellIndex,
    shellIndex,
    tableIndex,
    valueIndex,
  ]);

  const changeTargetCategoryDictionaryList = useCallback(
    (
      categoryDropdown: DictionaryCategoryDropdownList,
      mainDropdownIndex: number,
    ) => {
      const targetValue = currentTextareaInput;

      const targetCategoryIndex = categoryDropdown.list
        .map((listItem, categoryIndex) => {
          if (listItem.list.includes(targetValue)) {
            return {
              listIndex: listItem.list.indexOf(targetValue),
              categoryIndex,
            };
          }

          return null;
        })
        .find((item) => item !== null);

      if (targetCategoryIndex?.listIndex !== undefined) {
        changeTargetListIndex({
          mainIndex: mainDropdownIndex,
          listIndex: targetCategoryIndex?.listIndex || 0,
          categoryIndex: targetCategoryIndex?.categoryIndex || 0,
          key: categoryDropdown.key,
        });

        return true;
      }

      return false;
    },
    [changeTargetListIndex, currentTextareaInput],
  );

  const changeTargetDefaultDropdownList = useCallback(
    ({
      currentDropdownList,
      dropdownIndex,
      dropdownKey,
    }: {
      currentDropdownList: string[];
      dropdownIndex: number;
      dropdownKey: "DICTIONARY" | "AI";
    }) => {
      const targetValue = currentTextareaInput;

      const currentTargetIndex = currentDropdownList.indexOf(targetValue);

      if (currentTargetIndex > -1) {
        changeTargetListIndex({
          mainIndex: dropdownIndex,
          categoryIndex: 0,
          listIndex: currentTargetIndex,
          key: dropdownKey,
        });
        return true;
      }

      return false;
    },
    [changeTargetListIndex, currentTextareaInput],
  );

  const updateCurrentDropdownTargetFocus = useCallback(() => {
    entireDropdownList.some((dropdown, dropdownIndex) => {
      const isCategory = dropdown.key === "DICTIONARY" && dropdown.isCategory;
      if (dropdown.key === "DATASET") {
        return changeTargetDatasetDropdownList();
      }

      if (isCategory) {
        return changeTargetCategoryDictionaryList(dropdown, dropdownIndex);
      }

      if (!isCategory) {
        return changeTargetDefaultDropdownList({
          currentDropdownList: dropdown.list as string[],
          dropdownKey: dropdown.key,
          dropdownIndex,
        });
      }

      return false;
    });
  }, [
    changeTargetCategoryDictionaryList,
    changeTargetDatasetDropdownList,
    changeTargetDefaultDropdownList,
    entireDropdownList,
  ]);

  useEffect(() => {
    if (!isFocused) {
      setIsFocusInputField(false);
    }
    // NOTE : 포커스 상태가 바뀔 때만 실행될 수 있도록 주석처리
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  useEffect(() => {
    // NOTE :포커스 상태가 바뀔 때마다 가장 최신 entryData로 입력 값을 관리하는 State 업데이트
    setEnterValue(valueInfo.text);
  }, [valueInfo.text]);

  useEffect(() => {
    const targetValue = currentTextareaInput;

    if (isFocusInputField) return;

    if (!targetValue) {
      changeTargetListIndex(DEFAULT_TARGET_INDEX);
      return;
    }

    /** NOTE : clear 버튼을 통해 삭제하였을 때 targetValue가 바로 초기화 되지 않아
     * 별도로 포커스 인덱스를 초기화하기 위한 조건
     */
    if (
      targetValue &&
      valueInfo.text === undefined &&
      focusStatus.type === "default"
    ) {
      if (DATA_SET_DROPDOWN_KEYS.includes(groupTableKey)) {
        setSelectedDataset((preSelectedDataset) => ({
          ...preSelectedDataset,
          [shellKey as string]: "",
        }));
        setEnterValue("");
      }

      changeTargetListIndex(DEFAULT_TARGET_INDEX);
    }

    changeTargetListIndex(DEFAULT_TARGET_INDEX);

    updateCurrentDropdownTargetFocus();
  }, [
    updateCurrentDropdownTargetFocus,
    changeTargetListIndex,
    currentTextareaInput,
    focusStatus.type,
    isFocusInputField,
    valueInfo.text,
    groupTableKey,
    setSelectedDataset,
    selectedDataset,
    shellKey,
  ]);

  // NOTE : Dataset list가 있는 쉘 그룹의 경우 본인 이외의 쉘에 대한 데이터 관리를 위해 useEffect 추가
  useEffect(() => {
    const currentEntryDataValue = getShellValue(
      shellKey || "",
      tableIndex,
      groupShellIndex || 0,
      shellIndex || 0,
    )?.value[valueIndex || 0]?.text;

    if (DATA_SET_DROPDOWN_KEYS.includes(groupTableKey)) {
      setEnterValue(currentEntryDataValue);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    groupShellIndex,
    shellIndex,
    shellKey,
    tableIndex,
    valueIndex,
    entryData,
  ]);

  useEffect(() => {
    const draftDropdownList = initialDropdownList;
    changeDictionaryList(draftDropdownList);
  }, [shellKey, initialDropdownList, changeDictionaryList]);

  useEffect(() => {
    if (!textFieldTextareaRef.current) return;

    textFieldTextareaRef.current.innerText =
      changeDealTypeTextToDisplayName(shellKey, valueInfo.text) ?? "";

    if (shellKey === "NCT Name") {
      if (!valueInfo.text) setValidationMessage(null);
    }
  }, [changeDealTypeTextToDisplayName, entryData, shellKey, valueInfo.text]);

  useEffect(() => {
    if (shellKey === "NCT Name" && validationMessage === null) {
      checkedNCTNameValidation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shellKey === "NCT Name") {
      checkedNCTNameValidation(valueInfo.text);
    }

    // NOTE: valueInfo.text가 변경될 때만 동작하게 하기 위해 주석 처리
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueInfo.text]);

  return {
    selectedValue: changeDealTypeTextToDisplayName(shellKey, valueInfo.text),
    validationMessage,
    textFieldTextareaRef,
    isFocusInputField,
    targetListIndex,
    valueInfo,
    aiList,
    dataSetList: currentShellKeyDatasetList,
    dictionaryList,
    isDictionaryList,
    isDataSetList,
    isOriginalAiList,
    isOriginalDictionaryList,
    copyStatus,
    copyCurrentTextValue,
    handleChangedEnterValue,
    handleEnterValueKeyboard,
    handleFocusTextField,
    handleFocusTextFieldAndOpenListBox,
    handleFocusOutTextField,
    handleBlurTextField,
    handleChangeDataSetValue,
    changeSelectedValue,
    checkIndexOfValueInDefaultAiList,
    deleteCurrentTextField,
    changeMultiSelectValues,
    getRawMultiSelectValues,
  };
};
